import { AuthContext } from '../../types/auth';

const API_BASE =
  'https://qaqj2uc5pc.execute-api.us-east-2.amazonaws.com/prod/api';

/**
 * Fetches the read-only auth context for a username on the current device.
 * Powers the smart-login router (see resolveAuthState).
 *
 * Returns `{ ctx, ok }`. `ok` is true only when the request succeeded and the
 * body parsed; it is false on a non-2xx response OR a network/parse throw. In
 * either failure case `ctx` is still a complete, safe-fallback context (user
 * treated as unknown with no credential/request/invite). Callers use `ok` to
 * distinguish a transient blip (re-check, never mutate) from a true unknown
 * user, so a network error never routes to the mutating request-access flow.
 */
export async function getUserAuthContext(
  username: string,
): Promise<{ ctx: AuthContext; ok: boolean }> {
  const fallback: AuthContext = {
    userExists: false,
    hasCredentialForThisDevice: false,
    deviceRequest: null,
    invite: null,
    device: { ip: null, geoLocation: null, rpId: null },
  };

  try {
    const resp = await fetch(`${API_BASE}/auth/context`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.toLowerCase() }),
    });

    if (!resp.ok) {
      return { ctx: fallback, ok: false };
    }

    const data = (await resp.json()) as Partial<AuthContext>;

    // Normalize defensively — the endpoint should return the full shape, but
    // guard against partial payloads so the router always gets a complete object.
    return {
      ctx: {
        userExists: !!data.userExists,
        hasCredentialForThisDevice: !!data.hasCredentialForThisDevice,
        deviceRequest: data.deviceRequest ?? null,
        invite: data.invite ?? null,
        device: data.device ?? { ip: null, geoLocation: null, rpId: null },
      },
      ok: true,
    };
  } catch {
    return { ctx: fallback, ok: false };
  }
}
